import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { RocSignalLabel } from "@/components/roc/RocSignalLabel";


interface AboutEditorialProps {
  language: 'es' | 'en';
}

/**
 * Editorial section that strengthens the author entity (E-E-A-T) for /sobre-mi
 * and /en/about. No invented metrics, clients or testimonials — only how
 * engagements are structured and where Luis's specialization actually applies.
 */
export const AboutEditorial = ({ language }: AboutEditorialProps) => {
  const content = {
    es: {
      overline: "Cómo trabajo",
      title: "Cómo trabajo con clientes B2B",
      intro:
        "La mayoría de mis clientes son negocios B2B o e-commerce con un equipo interno pequeño o inexistente en SEO. No vendo horas de trabajo genérico: cada proyecto entra a resolver un problema específico de visibilidad, con hipótesis y forma de medir el avance definidas antes de empezar.",
      specializationsTitle: "Áreas de especialización",
      specializations: [
        {
          title: "SEO técnico",
          text: "Rastreo, indexación, renderizado y arquitectura de sitios grandes o migraciones. Aplica cuando el problema no es de contenido, sino de que Google no puede ver o priorizar lo que ya existe.",
        },
        {
          title: "SEO internacional",
          text: "Estrategia multi-mercado, hreflang y decisiones de estructura de dominio para negocios que venden en más de un país o idioma.",
        },
        {
          title: "Búsqueda bilingüe / mercado hispano",
          text: "Estrategia de contenido y palabras clave para marcas que buscan al consumidor hispano en Estados Unidos, sin traducir literalmente lo que funciona en inglés.",
        },
        {
          title: "E-commerce",
          text: "Arquitectura de categorías vs. producto, contenido de ficha y señales técnicas específicas de catálogos grandes.",
        },
        {
          title: "Amazon",
          text: "Optimización de listings y relación entre SEO orgánico de Amazon y Amazon Advertising.",
        },
        {
          title: "AI Search (GEO/AEO)",
          text: "Cómo se estructura el contenido para ser citado por buscadores de IA y asistentes, sin abandonar los fundamentos de SEO tradicional.",
        },
      ],
      fitTitle: "Con quién funciona bien esta consultoría",
      idealFor: [
        "Empresas B2B o e-commerce con tráfico orgánico existente que quieren una segunda opinión experta",
        "Equipos que necesitan priorización clara, no una lista interminable de tareas",
        "Negocios que venden o quieren vender al mercado hispano de EE. UU.",
        "Marcas con catálogos o sitios grandes que requieren SEO técnico serio",
      ],
      notForTitle: "Con quién no es un buen ajuste",
      notFor: [
        "Negocios que buscan un ranking garantizado en un plazo fijo",
        "Presupuestos que solo alcanzan para ejecución sin estrategia previa",
        "Proyectos que esperan resultados sin acceso al sitio o al equipo técnico",
      ],
      engagementTitle: "Qué implica una primera colaboración",
      engagementSteps: [
        "Diagnóstico inicial: revisión del sitio, del mercado y de la competencia directa",
        "Definición de prioridades con criterio de impacto, esfuerzo y evidencia disponible",
        "Propuesta de alcance y forma de medir el progreso antes de cualquier compromiso",
      ],
      relatedTitle: "Servicios relacionados",
      relatedSubtitle: "Explora en detalle las áreas donde suelo aportar más valor.",
      caseStudiesLabel: "Casos de éxito",
      caseStudiesDesc: "Ejemplos documentados de proyectos anteriores.",
      ctaTitle: "¿Quieres saber si tu situación encaja?",
      ctaText: "Una revisión estratégica inicial ayuda a identificar dónde está realmente la oportunidad antes de comprometer presupuesto.",
      ctaButton: "Solicitar diagnóstico estratégico",
    },
    en: {
      overline: "How I work",
      title: "How I work with B2B clients",
      intro:
        "Most of my clients are B2B or e-commerce businesses with a small or non-existent in-house SEO team. I don't sell generic hours — every project starts by solving a specific visibility problem, with a hypothesis and a way to measure progress defined before any work begins.",
      specializationsTitle: "Areas of specialization",
      specializations: [
        {
          title: "Technical SEO",
          text: "Crawling, indexing, rendering and architecture for large sites or migrations. Applies when the problem isn't content — it's that Google can't see or prioritize what already exists.",
        },
        {
          title: "International SEO",
          text: "Multi-market strategy, hreflang and domain-structure decisions for businesses selling in more than one country or language.",
        },
        {
          title: "Bilingual / Hispanic-market search",
          text: "Keyword and content strategy for brands targeting the US Hispanic consumer, without literally translating what already works in English.",
        },
        {
          title: "E-commerce",
          text: "Category vs. product architecture, listing content and technical signals specific to large catalogs.",
        },
        {
          title: "Amazon",
          text: "Listing optimization and the relationship between Amazon organic SEO and Amazon Advertising.",
        },
        {
          title: "AI search (GEO/AEO)",
          text: "How to structure content to be cited by AI-powered search and assistants, without abandoning traditional SEO fundamentals.",
        },
      ],
      fitTitle: "Who this works well for",
      idealFor: [
        "B2B or e-commerce companies with existing organic traffic looking for an expert second opinion",
        "Teams that need clear prioritization, not an endless task list",
        "Businesses that sell or want to sell into the US Hispanic market",
        "Brands with large catalogs or sites that need serious technical SEO",
      ],
      notForTitle: "Who this is not a good fit for",
      notFor: [
        "Businesses looking for a guaranteed ranking within a fixed timeframe",
        "Budgets that only cover execution with no prior strategy",
        "Projects that expect results without access to the site or the technical team",
      ],
      engagementTitle: "What a first engagement looks like",
      engagementSteps: [
        "Initial diagnosis: review of the site, the market and direct competitors",
        "Prioritization based on impact, effort and available evidence",
        "A scope proposal and a way to measure progress before any commitment",
      ],
      relatedTitle: "Related services",
      relatedSubtitle: "Explore in detail the areas where I typically add the most value.",
      caseStudiesLabel: "Case studies",
      caseStudiesDesc: "Documented examples of past projects.",
      ctaTitle: "Want to know if your situation is a fit?",
      ctaText: "An initial strategic review helps identify where the real opportunity is before committing budget.",
      ctaButton: "Request a Strategic SEO Review",
    },
  } as const;

  const t = content[language];

  const relatedLinks = language === 'es'
    ? [
        { to: "/consultor-seo", label: "Consultor SEO", description: "Estrategia SEO senior para negocios B2B." },
        { to: "/servicios/seo-tecnico", label: "SEO técnico", description: "Auditoría y arreglos priorizados de rastreo e indexación." },
        { to: "/servicios/seo-internacional", label: "SEO internacional", description: "Estrategia multi-mercado y hreflang." },
        { to: "/servicios/seo-bilingue-mercado-hispano", label: "SEO bilingüe / mercado hispano", description: "Estrategia para llegar al consumidor hispano en EE. UU." },
        { to: "/casos-de-exito", label: "Casos de éxito", description: "Ejemplos documentados de proyectos anteriores." },
      ]
    : [
        { to: "/en/seo-consultant", label: "SEO Consultant", description: "Senior SEO strategy for B2B businesses." },
        { to: "/en/services/technical-seo", label: "Technical SEO", description: "Prioritized crawling and indexing audits and fixes." },
        { to: "/en/services/international-seo", label: "International SEO", description: "Multi-market strategy and hreflang." },
        { to: "/en/services/hispanic-market-seo", label: "Hispanic-market SEO", description: "Strategy to reach the US Hispanic consumer." },
        { to: "/en/case-studies", label: "Case studies", description: "Documented examples of past projects." },
      ];

  const diagnosisPath = language === 'es' ? '/diagnostico-seo-gratis' : '/en/free-seo-diagnosis';

  return (
    <section className="bg-background py-16 lg:py-24">
      <div className="container-custom space-y-16">
        <div className="max-w-3xl">
          <RocSignalLabel index="05">{t.overline}</RocSignalLabel>
          <h2 className="font-display mt-4 text-2xl font-bold tracking-tight text-foreground lg:text-4xl">{t.title}</h2>
          <p className="mt-5 text-base leading-relaxed text-muted-foreground lg:text-lg">{t.intro}</p>
        </div>

        <div>
          <h3 className="font-display text-xl font-bold text-foreground lg:text-2xl">{t.specializationsTitle}</h3>
          <ul className="mt-6 grid gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
            {t.specializations.map((item) => (
              <li key={item.title} className="bg-background p-6">
                <span className="h-px w-6 bg-brand" aria-hidden="true" />
                <h4 className="mt-4 text-sm font-semibold uppercase tracking-[0.08em] text-foreground">{item.title}</h4>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
              </li>
            ))}
          </ul>
        </div>

        <div className="grid gap-10 border-t border-border pt-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <h3 className="font-mono text-[11px] uppercase tracking-[0.22em] text-brand">{t.fitTitle}</h3>
            <ul className="mt-5 space-y-3">
              {t.idealFor.map((point) => (
                <li key={point} className="flex gap-4 border-b border-border pb-3 text-sm text-foreground">
                  <span className="mt-2 h-px w-4 flex-shrink-0 bg-brand" aria-hidden="true" />
                  <span className="leading-relaxed">{point}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">{t.notForTitle}</h3>
            <ul className="mt-5 space-y-3">
              {t.notFor.map((point) => (
                <li key={point} className="flex gap-4 border-b border-border pb-3 text-sm text-muted-foreground">
                  <span className="mt-2 h-px w-4 flex-shrink-0 bg-border" aria-hidden="true" />
                  <span className="leading-relaxed">{point}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div>
          <h3 className="font-display text-xl font-bold text-foreground lg:text-2xl">{t.engagementTitle}</h3>
          <ol className="mt-6 border-t border-border">
            {t.engagementSteps.map((step, index) => (
              <li key={step} className="grid gap-2 border-b border-border py-5 sm:grid-cols-[64px_1fr] sm:gap-6">
                <span className="font-mono text-[11px] text-brand">{String(index + 1).padStart(2, '0')}</span>
                <span className="text-sm leading-relaxed text-muted-foreground">{step}</span>
              </li>
            ))}
          </ol>
        </div>

        <div>
          <h3 className="font-display text-xl font-bold text-foreground lg:text-2xl">{t.relatedTitle}</h3>
          <p className="mt-2 text-sm text-muted-foreground">{t.relatedSubtitle}</p>
          <ul className="mt-6 border-t border-border">
            {relatedLinks.map((link) => (
              <li key={link.to} className="border-b border-border">
                <Link
                  to={link.to}
                  className="group flex flex-col gap-1 py-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand sm:flex-row sm:items-baseline sm:gap-8"
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-foreground sm:w-[320px]">
                    {link.label}
                    <ArrowRight className="h-4 w-4 text-brand transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </span>
                  <span className="text-sm leading-relaxed text-muted-foreground">{link.description}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="border border-border p-8 lg:p-10">
          <h3 className="font-display max-w-2xl text-2xl font-bold text-foreground">{t.ctaTitle}</h3>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">{t.ctaText}</p>
          <Link
            to={diagnosisPath}
            className="roc-link mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          >
            {t.ctaButton}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
};

